import { Button, Modal } from "antd";
import { useEffect, useState } from "react";
import { computeClientTotal, draftToMarkup, markupToDraft, type MarkupDraft } from "../lib/quoteMarkup";
import { QuoteMarkupFields } from "./QuoteMarkupFields";

const formatPrice = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency, minimumFractionDigits: 0 }).format(amount);

/**
 * Dialog for adding, changing or removing an agent's private quote markup.
 * @param initial Values shown when the dialog opens
 * @param supplierTotal The price quoted by the supplier, used for the live client price; null hides it
 * @param canRemove Shows Remove markup (true when a markup already exists)
 * @param saveLabel Primary button text
 */
export const QuoteMarkupModal = ({
  open,
  initial,
  currency,
  supplierTotal,
  supplierTax = 0,
  optionalOffers = [],
  organisationName,
  canRemove,
  saving = false,
  saveLabel = "Save markup",
  onSave,
  onRemove,
  onCancel,
}: {
  open: boolean;
  initial: MarkupDraft;
  currency: string;
  supplierTotal: number | null;
  supplierTax?: number;
  optionalOffers?: Array<{ key: string; name: string; priceLabel: string }>;
  organisationName: string;
  canRemove: boolean;
  saving?: boolean;
  saveLabel?: string;
  onSave: (draft: MarkupDraft) => void;
  onRemove: () => void;
  onCancel: () => void;
}) => {
  const [draft, setDraft] = useState<MarkupDraft>(initial);
  useEffect(() => {
    if (open) setDraft(initial);
    // Reset only when the dialog opens, so edits are not lost while it is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      title="Your markup"
      open={open}
      onCancel={() => !saving && onCancel()}
      footer={[
        canRemove ? (
          <Button key="remove" danger disabled={saving} onClick={onRemove}>
            Remove markup
          </Button>
        ) : null,
        <Button key="cancel" disabled={saving} onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} disabled={Boolean((draft.markupValue || draft.gstEnabled) && (!draft.markupBasis || !draft.taxBase || optionalOffers.some((offer) => !draft.optionalPolicies?.[offer.key])))} onClick={() => onSave(draft)}>
          {saveLabel}
        </Button>,
      ]}
    >
      <p className="mb-4 text-sm text-gray-600">
        Added on top of the price {organisationName} quoted you. Only your agency can see it, and your downloads include
        it automatically.
      </p>
      <QuoteMarkupFields draft={draft} currency={currency} idPrefix="quote-markup-modal" onChange={setDraft} />
      {optionalOffers.map((offer) => <section key={offer.key} className="mt-4 rounded border p-3">
        <p className="font-semibold">{offer.name}</p><p>{offer.priceLabel}</p>
        <Button onClick={() => setDraft((previous) => ({ ...previous, optionalPolicies: { ...previous.optionalPolicies, [offer.key]: { markupType: "fixed", markupValue: 0, gstPercent: 0, markupBasis: "taxExclusive", taxBase: "markup" } } }))}>Use reviewed zero agent markup and tax</Button>
        <QuoteMarkupFields currency={currency} draft={markupToDraft(draft.optionalPolicies?.[offer.key])} onChange={(value) => setDraft((previous) => ({ ...previous, optionalPolicies: { ...previous.optionalPolicies, [offer.key]: draftToMarkup(value) || { markupType: value.markupType, markupValue: 0, gstPercent: 0, markupBasis: value.markupBasis, taxBase: value.taxBase } } }))} />
      </section>)}
      {supplierTotal !== null ? (
        <div className="mt-4 flex items-baseline justify-between rounded-lg bg-gray-50 px-3 py-2">
          <span className="text-xs font-semibold text-gray-600">Client price</span>
          <span className="text-lg font-semibold text-[#131B2A]">
            {formatPrice(computeClientTotal(supplierTotal, draftToMarkup(draft), supplierTax, currency), currency)}
          </span>
        </div>
      ) : null}
    </Modal>
  );
};
